<template>
  <div style="width: 100%; max-width: 1400px; margin: 0 auto; padding: 0 1rem;">
    <!-- Controls Section -->
    <v-row class="mb-6 align-center ma-0" justify="center" style="gap: 1rem;">
      <v-col cols="12" md="6" lg="5">
        <v-text-field
          v-model="search"
          label="Search Fighters"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          background-color="white"
          class="search-field"
        ></v-text-field>
      </v-col>
      <v-col cols="auto">
        <v-chip-group
          v-model="filters"
          multiple
          column
          active-class="primary--text"
        >
          <v-chip value="pdf" outlined class="filter-chip">
            <v-icon left size="20">mdi-download</v-icon>
            PDF
          </v-chip>
          <v-chip value="editable" outlined class="filter-chip">
            <v-icon left size="18">mdi-pencil</v-icon>
            Editable
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <!-- Gallery Grid -->
    <v-row class="ma-0" style="gap: 2rem; justify-content: center;">
      <v-col
        v-for="fighterDesign in filteredDesigns"
      :key="fighterDesign.fileName"
      class="d-flex child-flex pa-0"
      cols="12"
      sm="4"
      lg="3"
      xl="2"
    >
      <div 
        class="gallery-item"
        style="position: relative; width: 100%; aspect-ratio: 1 / 1; overflow: hidden; border-radius: 4px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);"
        @click="onClick(fighterDesign.fileName)"
      >
        <v-img
          :src="galleryPath(fighterDesign.image)"
          aspect-ratio="1"
          class="grey lighten-2 gallery-image"
          style="object-fit: cover; width: 100%; height: 100%; transition: transform 0.7s;"
        />
        
        <!-- Caption Overlay with Glassmorphism -->
        <div v-if="!fighterDesign.bannerLink" class="gallery-caption glass-effect">
          {{ fighterDesign.name }}
        </div>

        <!-- Badges -->
        <div v-if="!fighterDesign.bannerLink" class="badge-container">
          <img
            v-if="fighterDesign.patreonStoreLink"
            src="@/assets/patreon.svg"
            alt="Patreon"
            class="badge-icon static-badge"
          />
          <template v-else>
            <div 
              v-if="!fighterDesign.gumroadStoreLink"
              class="badge-icon editable-badge static-badge"
            >
              <v-icon color="white" size="20">mdi-pencil</v-icon>
            </div>
            <img
              v-if="fighterDesign.gumroadPermalink || fighterDesign.gumroadStoreLink"
              src="@/assets/gumroad.svg"
              alt="Gumroad"
              class="badge-icon static-badge gumroad-badge"
            />
          </template>
        </div>
      </div>
    </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.gallery-item {
  cursor: pointer;
}
.gallery-item:hover .gallery-image {
  transform: scale(1.05);
}

.gallery-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 12px 16px;
  color: #fff;
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-align: center;
  z-index: 5;
}

.glass-effect {
  background: rgba(26, 26, 64, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.1);
}

.badge-container {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
  z-index: 10;
}

.badge-icon {
  width: 32px;
  height: 32px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  transition: transform 0.2s;
}

.static-badge {
  /* Removed absolute positioning from individual badges */
  position: static !important;
}

.gumroad-badge {
  background-color: #ff5a5f; /* A pinkish color often associated with Gumroad or just a distinct premium color */
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.editable-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #c5a059; /* Brand Gold */
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.gallery-item:hover .badge-icon {
  transform: scale(1.1) rotate(5deg);
}

.search-field ::v-deep .v-input__slot {
  min-height: 48px !important;
}

.filter-chip {
  background-color: white !important;
  border-color: rgba(0,0,0,0.1) !important;
  color: #9ca3af !important; /* Lighter gray for inactive text */
}

.filter-chip .v-image, .filter-chip .v-icon {
  opacity: 0.6; /* Faint icons when inactive */
}

.filter-chip.v-chip--active .v-image, .filter-chip.v-chip--active .v-icon {
  opacity: 1; /* Full opacity when active */
}

.filter-chip.v-chip--active {
  border-color: #c5a059 !important;
  background-color: #c5a059 !important;
  color: #1a1a40 !important;
  font-weight: 600;
}
</style>

<script>
import { fighterDesigns } from "@/data/fighterDesigns.js";
import { analytics, logEvent } from "@/firebase.js";

export default {
  data() {
    return {
      fighterDesigns,
      search: "",
      filters: ["pdf", "editable"],
      searchTimeout: null,
    };
  },
  computed: {
    filteredDesigns() {
      let designs = this.fighterDesigns;

      // Filter by Search
      if (this.search) {
        const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, "");
        const query = normalize(this.search);
        
        designs = designs.filter((design) => {
          if (design.bannerLink) return true; // Always show banner items
          // Match against name
          if (normalize(design.name).includes(query)) return true;
          // Match against filename as fallback (often contains useful keywords)
          if (normalize(design.fileName).includes(query)) return true;
          return false;
        });
      }

      // Filter by Type (PDF / Editable)
      designs = designs.filter((design) => {
        if (design.bannerLink) return true; // Always show banner items

        const isPdf = !!design.patreonStoreLink || !!design.gumroadStoreLink;
        const isEditable = !isPdf;

        let match = false;
        if (this.filters.includes("pdf") && isPdf) match = true;
        if (this.filters.includes("editable") && isEditable) match = true;
        return match;
      });

      return designs;
    },
  },
  methods: {
    galleryPath(image) {
      return process.env.BASE_URL + "gallery/" + image;
    },
    onClick(fighterFileName) {
      const fighter = this.fighterDesigns.find(f => f.fileName === fighterFileName);
      if (fighter && (fighter.gumroadStoreLink || fighter.patreonStoreLink || fighter.bannerLink)) {
        window.location.href = fighter.gumroadStoreLink || fighter.patreonStoreLink || fighter.bannerLink;
        return;
      }
      this.$router.push(`/fighters/${fighterFileName}`);
    },
  },
  watch: {
    search(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        if (val) {
          logEvent(analytics, 'gallery_search', { search_term: val });
        }
      }, 1000); // Debounce for 1 second
    },
    filters(val) {
      logEvent(analytics, 'gallery_filter', {
        active_filters: val.join(',')
      });
    }
  }
};
</script>
